import { Checkbox, Flex, Input } from '@chakra-ui/react';
import parse from 'html-react-parser';
import './CustomCheckboxWithInput.scss';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import CustomErrorMessage from '@/common/CustomErrorMessage/CustomErrorMessage';
import { Constants } from '@/lib/constants';
import { sanitizeClientInput } from '@/utils/sanitizeClientInput';

export interface CustomCheckboxWithInputProps {
  name: string;
  label: string;
  onChange?: (value: string | boolean) => void;
  isChecked?: boolean;
  isRequired?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  othersIsCheckedButNotFilled?: boolean;
}

const CustomCheckboxWithInput = ({
  name,
  label,
  onChange,
  isRequired = false,
  isChecked = false,
  isDisabled = false,
  isReadOnly = false,
  othersIsCheckedButNotFilled = false,
}: CustomCheckboxWithInputProps) => {
  const [checked, setChecked] = useState(isChecked);
  const [inputValue, setInputValue] = useState(''); // Store input value
  const [hasBlurred, setHasBlurred] = useState(false); // Track if input has been blurred

  const { watch, setValue } = useFormContext();
  const othersTextValue = watch(name, '');

  useEffect(() => {
    if (isChecked && othersTextValue) {
      setInputValue(othersTextValue);
    }
  }, [othersTextValue]);

  //////////////////////////////////////////
  // Handle functions
  //////////////////////////////////////////

  //////////////////////////////////////////
  // Checkbox
  //////////////////////////////////////////

  const handleCheckboxChange = () => {
    const newChecked = !checked;
    setChecked(newChecked);

    if (onChange) {
      if (newChecked) {
        onChange(inputValue ?? ''); // Restore input value when checkbox is checked
      } else {
        onChange(false); // Return false when checkbox is unchecked
      }
    }
  };

  //////////////////////////////////////////
  // Input
  //////////////////////////////////////////

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    const cleanedValue = sanitizeClientInput(rawValue);

    setInputValue(cleanedValue);
    setValue(name, cleanedValue);

    if (onChange) {
      onChange(cleanedValue);
    }
  };

  const handleInputBlur = () => {
    setHasBlurred(true); // Mark input as touched on blur
  };

  //////////////////////////////////////////
  // If Others checkbox is checked, focus on the input field
  //////////////////////////////////////////

  useEffect(() => {
    if (checked) {
      const input: HTMLInputElement | null = document.querySelector(
        '.custom-checkbox-input'
      );
      input?.focus();
    }
  }, [checked]);

  return (
    <Flex className={'custom-checkbox-with-input-container'}>
      <Checkbox
        className={'custom-checkbox'}
        value={othersTextValue}
        onChange={handleCheckboxChange}
        isChecked={checked}
        isRequired={isRequired}
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
      />

      <Flex className={'custom-checkbox-label-and-input-container'}>
        {label && (
          <span className={'custom-checkbox-label'}>{parse(label)}</span>
        )}

        {checked && (
          <Input
            className={'custom-checkbox-input'}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            isRequired={isRequired}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            placeholder={'Please enter an input'}
            isInvalid={hasBlurred && othersIsCheckedButNotFilled}
            maxLength={150}
          />
        )}

        {hasBlurred && othersIsCheckedButNotFilled && (
          <CustomErrorMessage
            errorMessage={Constants.Input.ValidationErrors.EmptyInput}
          />
        )}
      </Flex>
    </Flex>
  );
};

export default CustomCheckboxWithInput;
